import { useCallback, useEffect, useRef, useState } from 'react';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { fetchAgreementHistory, HISTORY_PER_PAGE } from '../services/agreementHistory';
import { olderHistoryMayHoldPayment, paymentReceivedSince } from '../utils/paymentLinkWatch';
import type { AgreementCashPosition } from '../types/agreements.types';
import { agreementKeys } from './agreementKeys';
import { useLivePaymentLinkQuery } from './useLivePaymentLinkQuery';

export const BALANCE_POLL_MS = 5_000;
export const BALANCE_SETTLE_DEADLINE_MS = 45_000;

async function findPaymentSince(
    queryClient: QueryClient,
    id: string,
    sinceIso: string,
    isCurrent: () => boolean,
): Promise<string | null> {
    for (let pageNumber = 1; isCurrent(); pageNumber += 1) {
        const page = await queryClient.fetchQuery({
            queryKey: agreementKeys.historyPage(id, pageNumber, HISTORY_PER_PAGE),
            queryFn: () => fetchAgreementHistory(id, { page: pageNumber, perPage: HISTORY_PER_PAGE }),
        });
        const payment = paymentReceivedSince(page.entries, sinceIso);
        if (payment !== null || !olderHistoryMayHoldPayment(page, sinceIso, pageNumber)) return payment?.amount ?? null;
    }
    return null;
}

/** Polls the live link while one is outstanding; once it leaves the live read or is replaced, returns
 *  the amount of the MotorPlan payment the history records since that link was created. */
export function usePaymentLinkWatch(agreementId: string | undefined, enabled: boolean): string | null {
    const queryClient = useQueryClient();
    const [received, setReceived] = useState<string | null>(null);
    const previousCreatedAt = useRef<string | null>(null);
    const generation = useRef(0);
    const balancePoll = useRef<ReturnType<typeof setInterval> | null>(null);
    const { data: current } = useLivePaymentLinkQuery(agreementId, enabled, true);

    useEffect(() => {
        previousCreatedAt.current = null;
        generation.current += 1;
        setReceived(null);
        return () => {
            if (balancePoll.current !== null) clearInterval(balancePoll.current);
            balancePoll.current = null;
        };
    }, [agreementId]);

    const refreshAfterLink = useCallback(
        (id: string) => {
            const cashKey = agreementKeys.cashPosition(id);
            const heldBalance = () => queryClient.getQueryData<AgreementCashPosition>(cashKey)?.cashPosition;
            let baseline = heldBalance();
            const deadline = Date.now() + BALANCE_SETTLE_DEADLINE_MS;
            if (balancePoll.current !== null) clearInterval(balancePoll.current);
            const poll = setInterval(() => void checkBalance(), BALANCE_POLL_MS);
            balancePoll.current = poll;

            const checkBalance = async () => {
                await queryClient.invalidateQueries({ queryKey: cashKey, exact: true }, { cancelRefetch: false });
                if (balancePoll.current !== poll) return;
                const balance = heldBalance();
                const moved = baseline !== undefined && balance !== baseline;
                baseline ??= balance;
                if (!moved && Date.now() < deadline) return;
                clearInterval(poll);
                balancePoll.current = null;
                void queryClient.invalidateQueries({ queryKey: agreementKeys.financials(id) });
            };

            void queryClient.invalidateQueries({ queryKey: agreementKeys.leftToPay(id) });
            void queryClient.invalidateQueries({ queryKey: agreementKeys.financials(id) });
            void checkBalance();
        },
        [queryClient],
    );

    const announcePaymentSince = useCallback(
        (id: string, sinceIso: string) => {
            const watch = ++generation.current;
            const isCurrent = () => watch === generation.current;
            const announce = (amount: string | null) => {
                if (isCurrent()) setReceived(amount);
            };
            const historyKey = agreementKeys.history(id);
            void queryClient
                .cancelQueries({ queryKey: historyKey })
                .then(() => {
                    void queryClient.invalidateQueries({ queryKey: historyKey });
                    return findPaymentSince(queryClient, id, sinceIso, isCurrent);
                })
                .then(announce, () => announce(null));
        },
        [queryClient],
    );

    useEffect(() => {
        if (current === undefined || !agreementId) return;
        const endedCreatedAt = previousCreatedAt.current;
        const currentCreatedAt = current?.createdAt ?? null;
        previousCreatedAt.current = currentCreatedAt;
        if (endedCreatedAt === null || endedCreatedAt === currentCreatedAt) {
            if (current !== null) {
                generation.current += 1;
                setReceived(null);
            }
            return;
        }

        refreshAfterLink(agreementId);
        announcePaymentSince(agreementId, endedCreatedAt);
    }, [current, agreementId, refreshAfterLink, announcePaymentSince]);

    return received;
}
